import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaDate,
  type CitationParts,
  mlaDate,
  type PartialDate,
  parsePartialDate,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaUrl,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  beforeSubtitle,
  chicagoShortTitle,
  longDate,
  type MediaLabels,
  type MediaValues,
} from "@/lib/tool-kit/media-sources/common";

/*
 * Advertisements — print, television or radio, online, and outdoor — in APA 7, MLA 9
 * and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7 has no advertisement example. The generator applies the general reference
 *   pattern — author, date, title, source — with the advertiser as author and a
 *   bracketed description ("[Advertisement]", "[Television commercial]"), which APA
 *   adds to works outside the academic literature and lets writers word as needed
 *   (APA Style, "Elements of reference list entries", §9.21). An untitled ad is
 *   described in brackets in place of a title (§9.22). The source is where it ran: the
 *   periodical with volume, issue and page (§10.1), the broadcaster, the website and
 *   URL, or — for a billboard — the place, as APA does for works tied to a location.
 * - MLA 9: MLA Style Center, "How do I cite an advertisement in print and online?" —
 *   "Advertisement for Head and Shoulders. Newsweek, 17 Mar. 2008, p. 2." and a titled
 *   online ad ending "Pop-up ad."; "How do I cite a commercial?" — "GE refrigerator
 *   commercial. CBS, 10 Oct. 2019."; "How do I determine the publication date of a
 *   billboard?" (9th ed., citing §5.68) — "Advertisement for School of Rock: The
 *   Musical. 29 Jan. 2017, Times Square, New York City. Billboard." (the date viewed,
 *   the place, and the medium as a supplemental element). MLA begins with the ad's
 *   title or a description, not the advertiser.
 * - Chicago 18: no rule for print ads. The Chicago Style Q&A on television
 *   advertisements (updated for the 18th edition, pointing to §14.167) gives
 *   "Volkswagen. “Crazy Guy.” Television advertisement. Arnold Communications, Inc.,
 *   directed by Phil Morrison, 2000." and adds that a well-known ad can simply be
 *   described in the text. The generator applies that pattern to every medium, with a
 *   broadcast dated "aired … on CBS" as Chicago dates television, and drops an initial
 *   "The" from a periodical's name as the Quick Guide does (New York Times).
 */

export const ADVERTISEMENT_FIELDS = {
  advertiser: "advertiser",
  product: "product",
  productIsTitle: "productIsTitle",
  title: "title",
  agency: "agency",
  publication: "publication",
  volume: "volume",
  issue: "issue",
  page: "page",
  broadcastMedium: "broadcastMedium",
  network: "network",
  program: "program",
  website: "website",
  adFormat: "adFormat",
  url: "url",
  outdoorMedium: "outdoorMedium",
  location: "location",
  city: "city",
  region: "region",
  date: "date",
} as const;

const F = ADVERTISEMENT_FIELDS;

export type AdMedium = "print" | "broadcast" | "online" | "outdoor";

export interface AdvertisementLabels extends MediaLabels {
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Ad {
  medium: AdMedium;
  advertiser: string;
  product: string;
  productIsTitle: boolean;
  title: string;
  agency: string;
  publication: string;
  volume: string;
  issue: string;
  page: string;
  broadcast: "television" | "radio";
  network: string;
  program: string;
  website: string;
  adFormat: string;
  url: string;
  outdoor: string;
  location: string;
  city: string;
  region: string;
  date: PartialDate | null;
}

const OUTDOOR_MEDIA = new Set(["billboard", "poster", "transit"]);

function readAd(medium: AdMedium, values: MediaValues): Ad {
  const outdoor = get(values, F.outdoorMedium);
  return {
    medium,
    advertiser: get(values, F.advertiser),
    product: get(values, F.product),
    productIsTitle: get(values, F.productIsTitle) === "true",
    title: capitalizeSubtitle(get(values, F.title)),
    agency: get(values, F.agency),
    publication: get(values, F.publication),
    volume: get(values, F.volume),
    issue: get(values, F.issue),
    page: get(values, F.page),
    broadcast:
      get(values, F.broadcastMedium) === "radio" ? "radio" : "television",
    network: get(values, F.network),
    program: get(values, F.program),
    website: get(values, F.website),
    adFormat: get(values, F.adFormat),
    url: get(values, F.url),
    outdoor: OUTDOOR_MEDIA.has(outdoor) ? outdoor : "billboard",
    location: get(values, F.location),
    city: get(values, F.city),
    region: get(values, F.region),
    date: parsePartialDate(get(values, F.date)),
  };
}

const RE_ENDS_IN_MARK = /[?!]$/;
const RE_LEADING_THE = /^the\s+/i;

function quotedTitle(title: string, mark: "," | "." | ""): string {
  return RE_ENDS_IN_MARK.test(title) || mark === ""
    ? `“${title}”`
    : `“${title}${mark}”`;
}

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** What is being advertised: the product, or the advertiser itself. */
function subject(ad: Ad): string {
  return ad.product || ad.advertiser;
}

/** "Advertisement for Head and Shoulders" — with an italic title if the product is one. */
function forSubject(ad: Ad, lead: string): CitationParts {
  const what = subject(ad);
  if (what === "") {
    return [plain(lead)];
  }
  return [
    plain(`${lead} for `),
    ad.productIsTitle ? italic(what) : plain(what),
  ];
}

/** The medium in words, for descriptions: "television commercial", "billboard". */
function mediumWords(ad: Ad): string {
  if (ad.medium === "broadcast") {
    return `${ad.broadcast} commercial`;
  }
  if (ad.medium === "outdoor") {
    return ad.outdoor === "transit" ? "transit advertisement" : ad.outdoor;
  }
  if (ad.medium === "online" && ad.adFormat) {
    return ad.adFormat.toLowerCase();
  }
  return "advertisement";
}

/* ---------------------------------------------------------------------- APA */

function apaBracket(ad: Ad): string {
  if (ad.medium === "broadcast") {
    return capitalizeFirst(`${ad.broadcast} commercial`);
  }
  if (ad.medium === "outdoor") {
    return capitalizeFirst(`${ad.outdoor} advertisement`);
  }
  return "Advertisement";
}

/** Print and online ads sit inside another work, so their titles stay roman. */
function apaTitleIsItalic(ad: Ad): boolean {
  return ad.medium === "broadcast" || ad.medium === "outdoor";
}

function apaTitle(ad: Ad): CitationParts {
  if (ad.title) {
    const title = apaTitleIsItalic(ad) ? italic(ad.title) : plain(ad.title);
    return [title, plain(` [${apaBracket(ad)}].`)];
  }
  const description = apaBracket(ad);
  return [plain("["), ...forSubject(ad, description), plain("].")];
}

function apaPrintSource(ad: Ad): CitationParts {
  if (ad.publication === "") {
    return [];
  }
  const parts: CitationParts = [plain(" "), italic(ad.publication)];
  if (ad.volume) {
    parts.push(plain(", "), italic(ad.volume));
    parts.push(plain(ad.issue ? `(${ad.issue})` : ""));
  }
  parts.push(plain(ad.page ? `, ${ad.page}.` : "."));
  return parts;
}

function apaSource(ad: Ad): CitationParts {
  if (ad.medium === "print") {
    return apaPrintSource(ad);
  }
  if (ad.medium === "broadcast") {
    return ad.network ? [plain(` ${ad.network}${closer(ad.network)}`)] : [];
  }
  if (ad.medium === "online") {
    const site =
      ad.website && !sameName(ad.website, ad.advertiser)
        ? ` ${ad.website}${closer(ad.website)}`
        : "";
    return [plain(`${site}${ad.url ? ` ${ad.url}` : ""}`)];
  }
  const place = [ad.location, ad.city, ad.region].filter(Boolean).join(", ");
  return place ? [plain(` ${withPeriod(place)}`)] : [];
}

function apaReference(ad: Ad): CitationParts {
  const date = `(${apaDate(ad.date)}).`;
  if (ad.advertiser === "") {
    return tidyParts([...apaTitle(ad), plain(` ${date}`), ...apaSource(ad)]);
  }
  return tidyParts([
    plain(`${ad.advertiser}${closer(ad.advertiser)} ${date} `),
    ...apaTitle(ad),
    ...apaSource(ad),
  ]);
}

function apaInText(ad: Ad, narrative: boolean): CitationParts {
  const year = ad.date ? String(ad.date.year) : "n.d.";
  if (ad.advertiser) {
    return [
      plain(
        narrative ? `${ad.advertiser} (${year})` : `(${ad.advertiser}, ${year})`
      ),
    ];
  }
  if (ad.title) {
    const short = beforeSubtitle(titleCase(ad.title));
    const title = apaTitleIsItalic(ad) ? italic(short) : plain(`“${short}”`);
    return narrative
      ? [title, plain(` (${year})`)]
      : [plain("("), title, plain(`, ${year})`)];
  }
  const bracket: CitationParts = [
    plain("["),
    ...forSubject(ad, apaBracket(ad)),
    plain("]"),
  ];
  return narrative
    ? [...bracket, plain(` (${year})`)]
    : [plain("("), ...bracket, plain(`, ${year})`)];
}

function apaOutput(ad: Ad, labels: AdvertisementLabels): CitationOutput {
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(ad) },
      { label: labels.parenthetical, parts: apaInText(ad, false) },
      { label: labels.narrative, parts: apaInText(ad, true) },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

/** MLA's description of an untitled ad: "GE refrigerator commercial". */
function mlaDescription(ad: Ad): CitationParts {
  if (ad.medium === "broadcast") {
    const what = subject(ad);
    return [plain(what ? `${what} commercial` : "Commercial")];
  }
  return forSubject(ad, "Advertisement");
}

/** MLA's first element: the ad's title in quotation marks, or a description. */
function mlaFirstElement(ad: Ad): CitationParts {
  if (ad.title) {
    return [plain(quotedTitle(titleCase(ad.title), "."))];
  }
  return [...mlaDescription(ad), plain(".")];
}

function mlaPrintContainer(ad: Ad): CitationParts {
  const details = [
    ad.volume ? `vol. ${ad.volume}` : "",
    ad.issue ? `no. ${ad.issue}` : "",
    mlaDate(ad.date),
    ad.page ? `p. ${ad.page}` : "",
  ].filter(Boolean);
  if (ad.publication === "") {
    return [plain(` ${withPeriod(details.join(", "))}`)];
  }
  return [
    plain(" "),
    italic(ad.publication),
    plain(details.length > 0 ? `, ${details.join(", ")}.` : "."),
  ];
}

/** A container in italics followed by its details, or the details alone. */
function mlaNamedContainer(name: string, details: string[]): CitationParts {
  const rest = details.filter(Boolean).join(", ");
  if (name) {
    return [plain(" "), italic(name), plain(rest ? `, ${rest}.` : ".")];
  }
  return [plain(` ${withPeriod(rest)}`)];
}

function mlaContainer(ad: Ad): CitationParts {
  if (ad.medium === "print") {
    return mlaPrintContainer(ad);
  }
  if (ad.medium === "broadcast") {
    if (ad.program) {
      return mlaNamedContainer(ad.program, [ad.network, mlaDate(ad.date)]);
    }
    const rest = [ad.network, mlaDate(ad.date)].filter(Boolean).join(", ");
    return [plain(` ${withPeriod(rest)}`)];
  }
  if (ad.medium === "online") {
    return mlaNamedContainer(ad.website, [
      mlaDate(ad.date),
      ad.url ? mlaUrl(ad.url) : "",
    ]);
  }
  const place = [mlaDate(ad.date), ad.location, ad.city].filter(Boolean);
  return [plain(` ${withPeriod(place.join(", "))}`)];
}

/** The closing supplemental element: the medium, and the advertiser if untold. */
function mlaSupplement(ad: Ad): CitationParts {
  const words = capitalizeFirst(mediumWords(ad));
  if (!ad.title) {
    const describesMedium =
      ad.medium === "outdoor" || (ad.medium === "online" && ad.adFormat);
    return describesMedium ? [plain(` ${words}.`)] : [];
  }
  const advertiserShown =
    ad.medium === "online" && sameName(ad.website, subject(ad));
  if (advertiserShown || subject(ad) === "") {
    return [plain(` ${words}.`)];
  }
  return [plain(" "), ...forSubject(ad, words), plain(".")];
}

function mlaInText(ad: Ad): CitationParts {
  if (ad.title) {
    return [plain(`(“${beforeSubtitle(titleCase(ad.title))}”)`)];
  }
  return [plain("("), ...mlaDescription(ad), plain(")")];
}

function mlaOutput(ad: Ad, labels: AdvertisementLabels): CitationOutput {
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      {
        label: labels.worksCited,
        parts: tidyParts([
          ...mlaFirstElement(ad),
          ...mlaContainer(ad),
          ...mlaSupplement(ad),
        ]),
      },
      { label: labels.inText, parts: mlaInText(ad) },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

/** Chicago's word for the medium, as in its Q&A: "television advertisement". */
function chicagoMedium(ad: Ad): string {
  if (ad.medium === "broadcast") {
    return `${ad.broadcast} advertisement`;
  }
  if (ad.medium === "outdoor") {
    return `${ad.outdoor} advertisement`;
  }
  if (ad.medium === "online" && ad.adFormat) {
    return ad.adFormat.toLowerCase();
  }
  return "advertisement";
}

/** "advertisement" or "advertisement for Worn Wear" — the product only if named. */
function chicagoDescription(ad: Ad, capitalized: boolean): CitationParts {
  const words = capitalized
    ? capitalizeFirst(chicagoMedium(ad))
    : chicagoMedium(ad);
  const what = ad.advertiser ? ad.product : subject(ad);
  if (what === "") {
    return [plain(words)];
  }
  return [
    plain(`${words} for `),
    ad.productIsTitle ? italic(what) : plain(what),
  ];
}

/** A periodical's name as Chicago cites it: without an initial "The". */
function chicagoPeriodical(name: string): string {
  return name.replace(RE_LEADING_THE, "");
}

/** Where and when it ran: "New York Times, November 25, 2011", "aired …, on CBS". */
function chicagoWhereWhen(ad: Ad): CitationParts {
  const when = longDate(ad.date);
  if (ad.medium === "print") {
    const tail = [when, ad.page].filter(Boolean).join(", ");
    if (ad.publication === "") {
      return [plain(tail)];
    }
    return [
      italic(chicagoPeriodical(ad.publication)),
      plain(tail ? `, ${tail}` : ""),
    ];
  }
  if (ad.medium === "broadcast") {
    const aired = when ? `aired ${when}` : "";
    const on = ad.network ? `on ${ad.network}` : "";
    return [plain([aired, on].filter(Boolean).join(", "))];
  }
  if (ad.medium === "online") {
    return [plain([ad.website, when, ad.url].filter(Boolean).join(", "))];
  }
  return [plain([ad.location, ad.city, when].filter(Boolean).join(", "))];
}

function hasText(parts: CitationParts): boolean {
  return parts.some((part) => part.text.trim() !== "");
}

function chicagoNote(ad: Ad): CitationParts {
  const parts: CitationParts = [];
  if (ad.advertiser) {
    parts.push(plain(`${ad.advertiser}, `));
  }
  if (ad.title) {
    parts.push(plain(`${quotedTitle(titleCase(ad.title), ",")} `));
  }
  parts.push(...chicagoDescription(ad, !(ad.advertiser || ad.title)));
  if (ad.agency) {
    parts.push(plain(`, ${ad.agency}`));
  }
  const whereWhen = chicagoWhereWhen(ad);
  if (hasText(whereWhen)) {
    parts.push(plain(", "), ...whereWhen);
  }
  parts.push(plain("."));
  return tidyParts(parts);
}

function chicagoShortNote(ad: Ad): CitationParts {
  const lead = ad.advertiser ? `${ad.advertiser}, ` : "";
  if (ad.title) {
    const short = quotedTitle(chicagoShortTitle(titleCase(ad.title)), ".");
    return [plain(`${lead}${short}`)];
  }
  return [plain(lead), ...chicagoDescription(ad, !ad.advertiser), plain(".")];
}

function chicagoBibliography(ad: Ad): CitationParts {
  const parts: CitationParts = [];
  if (ad.advertiser) {
    parts.push(plain(`${ad.advertiser}${closer(ad.advertiser)} `));
  }
  if (ad.title) {
    parts.push(plain(`${quotedTitle(titleCase(ad.title), ".")} `));
  }
  parts.push(...chicagoDescription(ad, true), plain(". "));
  const whereWhen = chicagoWhereWhen(ad);
  const rest: CitationParts = ad.agency
    ? [plain(`${ad.agency}${hasText(whereWhen) ? ", " : ""}`), ...whereWhen]
    : whereWhen;
  const first = rest[0];
  if (first && !first.italic) {
    rest[0] = { ...first, text: capitalizeFirst(first.text) };
  }
  if (hasText(rest)) {
    parts.push(...rest, plain("."));
  }
  return tidyParts(parts);
}

function chicagoOutput(ad: Ad, labels: AdvertisementLabels): CitationOutput {
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(ad) },
      { label: labels.shortNote, parts: chicagoShortNote(ad) },
      { label: labels.bibliography, parts: chicagoBibliography(ad) },
    ],
    note: labels.chicagoNote,
  };
}

export function advertisementIsReady(values: MediaValues): boolean {
  const named =
    get(values, F.advertiser) !== "" ||
    get(values, F.product) !== "" ||
    get(values, F.title) !== "";
  return named && parsePartialDate(get(values, F.date)) !== null;
}

export function formatAdvertisement(
  medium: AdMedium,
  values: MediaValues,
  labels: AdvertisementLabels
): CitationOutput[] {
  const ad = readAd(medium, values);
  return [
    apaOutput(ad, labels),
    mlaOutput(ad, labels),
    chicagoOutput(ad, labels),
  ];
}
